import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import Storage from 'src/utils/storage/storage';
import { getDynamicDrawerData } from '../../store/planner-slice-async-thunks';
import {
  DrawerProductItem,
  IProductComment
} from '../../types/ppg-sku-drawer.model';
import useProductComment from '../../hooks/product-comment-hook';
import {
  addNewCommentForProductDrawerParentItem,
  addNewCommentForProductDrawerChildItem,
  updateCommentForProductDrawerParentItem,
  updateCommentForProductDrawerChildItem,
  deleteCommentFromProductDrawerParentItem,
  deleteCommentFromProductDrawerChildItem,
  resetEanZrepDetails
} from '../../store/planner-slice';

export default function useProductDrawer() {
  const { id: scenarioId } = useParams();
  const { source: sourceId } = Storage.getSourceId();
  const { moduleNameToModuleIdMap } = useAppSelector(
    (state) => state.simulatorHome
  );
  const { toggledView } = useAppSelector((state) => state.plannerV2);
  const { productId, isLoading, drawerData, productKeyRef } = useAppSelector(
    (state) => state.plannerV2.drawerViewMetadata
  );
  const { planner: plannerAccessConfig = {} } = useAppSelector(
    (state) => state.simulatorHome.moduleAccessMap
  );
  const { handleAddComment, handleDeleteComment, handleUpdateComment } =
    useProductComment();
  const appDispatch = useAppDispatch();

  const [childDetailsExpandableMap, setChildDetailsExpandableMap] = useState(
    {}
  );

  const collapseAll = () => {
    setChildDetailsExpandableMap(
      Object.keys(childDetailsExpandableMap)
        .map((key) => ({
          [`${key}`]: false
        }))
        .reduce((prev, curr) => ({ ...prev, ...curr }), {})
    );
  };

  const toggleExpansion = (indexOfChild: number) => {
    const isOpen = childDetailsExpandableMap[indexOfChild];
    setChildDetailsExpandableMap({
      ...childDetailsExpandableMap,
      [`${indexOfChild}`]: !isOpen
    });
  };

  useEffect(() => {
    appDispatch(
      getDynamicDrawerData({
        apiPayload: {
          [productKeyRef]: productId,
          toggle: toggledView
        },
        urlParams: {
          moduleId: moduleNameToModuleIdMap.planner,
          scenarioId: Number(scenarioId),
          sourceId
        }
      })
    ).then(() => {
      // TODO: Create map from child elements of drawer
      setChildDetailsExpandableMap(
        Array.from(Array([]).keys()).reduce(
          (prev, curr) => ({ ...prev, [curr]: true }),
          {}
        )
      );
    });
  }, []);
  // to reset the existing product mapping state while loading the product drawer view
  useEffect(() => {
    appDispatch(resetEanZrepDetails());
  }, [])

  return {
    handleAddComment: (
      comment: string,
      currentDrawerInfo: DrawerProductItem,
      parentDrawerInfo: DrawerProductItem,
      levelMapping: { [key: string]: string }
    ) => {
      // TODO: Remove hard coding of level mapping
      const levelMapping2 = {
        product_field_1: 'product_field_1',
        product_field_2: 'product_field_2'
      };
      let productMappingData = {};
      // eslint-disable-next-line array-callback-return
      Object.keys(levelMapping2).forEach((data) => {
        if (currentDrawerInfo.parent.data[levelMapping2[data]] !== 'NAN') {
          productMappingData = Object.assign(productMappingData, {
            [data]: currentDrawerInfo.parent.data[levelMapping2[data]]
          });
        }
      });
      const payload = {
        comment_text: comment,
        productMapping: productMappingData
      };
      return handleAddComment(payload).then((response) => {
        if (!parentDrawerInfo) {
          appDispatch(
            addNewCommentForProductDrawerParentItem({
              comment: response.payload as IProductComment,
              currentDrawerInfo
            })
          );
        } else {
          appDispatch(
            addNewCommentForProductDrawerChildItem({
              commentIndex: 1,
              comment: response.payload as IProductComment,
              currentDrawerInfo,
              parentDrawerInfo
            })
          );
        }
      });
    },
    handleUpdateComment: (
      commentId: number,
      updatedComment: string,
      currentDrawerInfo: DrawerProductItem,
      parentDrawerInfo: DrawerProductItem,
      itemIndex: number = null
    ) =>
      handleUpdateComment(commentId, updatedComment).then((response) => {
        if (!parentDrawerInfo) {
          appDispatch(
            updateCommentForProductDrawerParentItem({
              comment: response.payload as IProductComment,
              currentDrawerInfo
            })
          );
        } else {
          appDispatch(
            updateCommentForProductDrawerChildItem({
              itemIndex,
              comment: response.payload as IProductComment,
              currentDrawerInfo,
              parentDrawerInfo
            })
          );
        }
      }),
    handleDeleteComment: (
      commentId: number,
      currentDrawerInfo: DrawerProductItem,
      parentDrawerInfo: DrawerProductItem,
      itemIndex: number = null
    ) =>
      handleDeleteComment(commentId).then(() => {
        if (!parentDrawerInfo) {
          appDispatch(
            deleteCommentFromProductDrawerParentItem({
              itemIndex,
              commentId,
              currentDrawerInfo
            })
          );
        } else {
          appDispatch(
            deleteCommentFromProductDrawerChildItem({
              itemIndex,
              commentId,
              currentDrawerInfo,
              parentDrawerInfo
            })
          );
        }
      }),
    isLoading,
    childDetailsExpandableMap,
    productId,
    appDispatch,
    toggleExpansion,
    collapseAll,
    plannerAccessConfig,
    drawerData,
    toggledView
  };
}
